// src/Services/paymentService.js
// Chooses how a buyer pays: naira through Paystack, or USD through Bachs.
// Buyers in Nigeria get naira by default, everyone else gets USD, and either
// can switch (saved in localStorage). Server-side, both routes end in the
// same applyPayment grant (functions/payments.js).
import paystackService from "./paystackService";
import bachsService from "./bachsService";
import useCoinStore from "../stores/coinStore";
import { rememberPendingPurchase, showPurchaseSuccess } from "./purchaseTracker";
import { paymentProviderFor } from "./paymentRouting";

const CURRENCY_KEY = "nameit_pay_currency";

export function detectCurrency() {
  try {
    const saved = localStorage.getItem(CURRENCY_KEY);
    if (saved === "NGN" || saved === "USD") return saved;
  } catch {
    // Storage can be blocked; fall through to detection.
  }
  let timeZone = "";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    // Ignore: treat as unknown.
  }
  const inNigeria = timeZone === "Africa/Lagos" || /-NG$/i.test(navigator.language || "");
  return inNigeria ? "NGN" : "USD";
}

export function saveCurrency(currency) {
  try {
    localStorage.setItem(CURRENCY_KEY, currency);
  } catch {
    // Not critical: the choice just won't survive a reload.
  }
}

/**
 * Pays for a product from Services/products.js in the given currency.
 * Resolves when the purchase is paid; rejects with { cancelled: true } if the
 * buyer backs out. Paystack purchases from the mobile app's deep link
 * (mobileRef) are always naira.
 *
 * By default the buyer is then shown what happened (a "Payment successful"
 * dialog, or "processing" while a dollar purchase lands). Pass status: false if
 * the calling screen shows its own result. baselineMaxPlayers is the room's
 * current size, used to notice that bought seats have arrived.
 */
export async function payForProduct({ currency, status = true, baselineMaxPlayers = 0, fallbackProvider = null, ...options }) {
  if (paymentProviderFor({ currency, mobileRef: options.mobileRef, fallbackProvider }) === "bachs") {
    const account = useCoinStore.getState();
    const baseline = {
      coins: account.coinBalance,
      isPro: account.isPro,
      proExpiry: account.proExpiry,
      maxPlayers: baselineMaxPlayers,
    };
    const result = await bachsService.payForProduct({ ...options, currency });
    rememberPendingPurchase({ productId: options.productId, roomId: options.roomId ?? null, transactionId: result.reference, baseline });
    window.location.assign(result.checkoutUrl);
    return result;
  }

  const grant = await paystackService.payForProduct(options);
  if (status) showPurchaseSuccess(options.productId, { roomId: options.roomId ?? null });
  return grant;
}
